import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Speed,
  Timeline,
  Assignment,
  People,
  MonetizationOn,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Refresh
} from '@mui/icons-material';
import analyticsService from '../../../services/analyticsService';

interface DateRange {
  start: Date;
  end: Date;
}

interface PerformanceDashboardProps {
  dateRange: DateRange;
}

interface KPIMetric {
  id: string;
  title: string;
  value: string;
  target: string;
  percentage: number;
  trend: 'up' | 'down' | 'neutral';
  change: number;
  status: 'excellent' | 'good' | 'warning' | 'critical';
  description: string;
}

interface SystemMetric {
  name: string;
  value: number;
  maxValue: number;
  unit: string;
  status: 'normal' | 'warning' | 'critical';
}

interface ActivityItem {
  id: string;
  type: 'project' | 'team' | 'financial' | 'system';
  title: string;
  description: string;
  timestamp: Date;
  status: 'success' | 'warning' | 'error' | 'info';
  impact: 'high' | 'medium' | 'low';
}

const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({ dateRange }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [timeframe, setTimeframe] = useState('week');
  const [selectedMetrics, setSelectedMetrics] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpiMetrics, setKpiMetrics] = useState<KPIMetric[]>([]);
  const [systemMetrics, setSystemMetrics] = useState<SystemMetric[]>([]);
  const [activities, setActivities] = useState<ActivityItem[]>([]);

  // Fetch performance data from API
  useEffect(() => {
    const fetchPerformanceData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await analyticsService.getPerformanceData(dateRange);
        
        // Transform API data to component format
        if (data.kpi_metrics) {
          const transformedKPIs: KPIMetric[] = data.kpi_metrics.map((metric: any, index: number) => ({
            id: `kpi-${index}`,
            title: metric.title,
            value: typeof metric.value === 'number' ? `${metric.value}${metric.unit || ''}` : metric.value.toString(),
            target: '100%', // Default target
            percentage: metric.value,
            trend: metric.trend,
            change: parseFloat(metric.trend.replace(/[+%]/g, '')) || 0,
            status: metric.color,
            description: `${metric.title} performance metric`
          }));
          setKpiMetrics(transformedKPIs);
        }

        if (data.system_metrics) {
          const transformedSystemMetrics: SystemMetric[] = data.system_metrics.map((metric: any) => ({
            name: metric.name,
            value: metric.value,
            maxValue: metric.target || 100,
            unit: metric.unit,
            status: metric.status === 'excellent' || metric.status === 'good' ? 'normal' : (metric.status === 'warning' ? 'warning' : 'critical')
          }));
          setSystemMetrics(transformedSystemMetrics);
        }

        if (data.activity_feed) {
          const transformedActivities: ActivityItem[] = data.activity_feed.map((activity: any) => ({
            id: activity.id.toString(),
            type: activity.type,
            title: activity.title,
            description: activity.description,
            timestamp: new Date(activity.timestamp),
            status: activity.severity,
            impact: 'medium' // Default impact
          }));
          setActivities(transformedActivities);
        }
        
      } catch (err) {
        console.error('Error fetching performance data:', err);
        setError('Failed to load performance data. Please try again.');
        // Keep using fallback data if API fails
        setKpiMetrics(getFallbackKPIData());
        setSystemMetrics(getFallbackSystemMetrics());
        setActivities(getFallbackActivities());
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceData();
  }, [dateRange, timeframe]);

  // Fallback data for when API is unavailable
  const getFallbackKPIData = (): KPIMetric[] => [
    {
      id: 'revenue-growth',
      title: 'Revenue Growth',
      value: '$124,500',
      target: '$120,000',
      percentage: 103.8,
      trend: 'up',
      change: 15.2,
      status: 'excellent',
      description: 'Monthly recurring revenue exceeding targets'
    },
    {
      id: 'project-delivery',
      title: 'Project Delivery Rate',
      value: '94%',
      target: '95%',
      percentage: 94,
      trend: 'down',
      change: -1.2,
      status: 'good',
      description: 'On-time project completion rate'
    },
    {
      id: 'client-satisfaction',
      title: 'Client Satisfaction',
      value: '4.8/5',
      target: '4.5/5',
      percentage: 96,
      trend: 'up',
      change: 3.4,
      status: 'excellent',
      description: 'Average client rating across all projects'
    },
    {
      id: 'team-utilization',
      title: 'Team Utilization',
      value: '87%',
      target: '85%',
      percentage: 87,
      trend: 'up',
      change: 2.1,
      status: 'good',
      description: 'Billable hours vs available hours'
    },
    {
      id: 'profit-margin',
      title: 'Profit Margin',
      value: '32%',
      target: '30%',
      percentage: 107,
      trend: 'up',
      change: 4.8,
      status: 'excellent',
      description: 'Net profit margin across all projects'
    },
    {
      id: 'lead-conversion',
      title: 'Lead Conversion',
      value: '28%',
      target: '25%',
      percentage: 112,
      trend: 'up',
      change: 7.3,
      status: 'excellent',
      description: 'Leads to paying customers conversion rate'
    }
  ];

  // Fallback system metrics for when API is unavailable
  const getFallbackSystemMetrics = (): SystemMetric[] => [
    { name: 'Server CPU Usage', value: 65, maxValue: 100, unit: '%', status: 'normal' },
    { name: 'Memory Usage', value: 78, maxValue: 100, unit: '%', status: 'warning' },
    { name: 'Database Performance', value: 92, maxValue: 100, unit: '%', status: 'normal' },
    { name: 'API Response Time', value: 245, maxValue: 500, unit: 'ms', status: 'normal' },
    { name: 'Active Users', value: 1247, maxValue: 2000, unit: '', status: 'normal' },
    { name: 'Storage Usage', value: 67, maxValue: 100, unit: '%', status: 'normal' }
  ];

  // Fallback activities for when API is unavailable
  const getFallbackActivities = (): ActivityItem[] => [
    {
      id: '1',
      type: 'project',
      title: 'E-commerce Platform Delivered',
      description: 'Successfully delivered ABC Corp e-commerce platform',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      status: 'success',
      impact: 'high'
    },
    {
      id: '2',
      type: 'team',
      title: 'New Developer Onboarded',
      description: 'Sarah Johnson joined the frontend development team',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      status: 'info',
      impact: 'medium'
    },
    {
      id: '3',
      type: 'financial',
      title: 'Invoice Payment Received',
      description: 'XYZ Industries paid $45,000 invoice',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
      status: 'success',
      impact: 'high'
    },
    {
      id: '4',
      type: 'system',
      title: 'Server Maintenance Scheduled',
      description: 'Planned maintenance window: Oct 20, 2:00 AM - 4:00 AM',
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000),
      status: 'warning',
      impact: 'medium'
    },
    {
      id: '5',
      type: 'project',
      title: 'Project Milestone Achieved',
      description: 'Mobile app beta testing phase completed',
      timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
      status: 'success',
      impact: 'medium'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getSystemStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getActivityIcon = (type: string, status: string) => {
    if (status === 'success') return <CheckCircle color="success" />;
    if (status === 'warning') return <Warning color="warning" />;
    if (status === 'error') return <ErrorIcon color="error" />;
    
    switch (type) {
      case 'project': return <Assignment color="primary" />;
      case 'team': return <People color="secondary" />;
      case 'financial': return <MonetizationOn color="success" />;
      case 'system': return <Speed color="info" />;
      default: return <Timeline />;
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    setTimeout(() => {
      setRefreshing(false);
    }, 2000);
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) return 'Just now';
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;
    
    const days = Math.floor(hours / 24);
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  return (
    <Box>
      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading performance data...</Typography>
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      {!loading && (
        <>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Timeframe</InputLabel>
          <Select
            value={timeframe}
            label="Timeframe"
            onChange={(e) => setTimeframe(e.target.value)}
          >
            <MenuItem value="day">Today</MenuItem>
            <MenuItem value="week">This Week</MenuItem>
            <MenuItem value="month">This Month</MenuItem>
            <MenuItem value="quarter">This Quarter</MenuItem>
            <MenuItem value="year">This Year</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Metrics</InputLabel>
          <Select
            value={selectedMetrics}
            label="Metrics"
            onChange={(e) => setSelectedMetrics(e.target.value)}
          >
            <MenuItem value="all">All Metrics</MenuItem>
            <MenuItem value="financial">Financial</MenuItem>
            <MenuItem value="operational">Operational</MenuItem>
            <MenuItem value="team">Team Performance</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
          onClick={handleRefresh}
          disabled={refreshing}
          size="small"
        >
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Box>

      {/* KPI Metrics Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {kpiMetrics.map((metric) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={metric.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="div" gutterBottom>
                    {metric.title}
                  </Typography>
                  <Chip
                    size="small"
                    label={metric.status}
                    color={getStatusColor(metric.status) as any}
                    variant="outlined"
                  />
                </Box>
                
                <Typography variant="h4" color="primary" gutterBottom>
                  {metric.value}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Target: {metric.target}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(metric.percentage, 100)}
                    sx={{ flexGrow: 1, mr: 1, height: 8, borderRadius: 4 }}
                    color={metric.percentage >= 100 ? 'success' : 'primary'}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {metric.percentage}%
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {metric.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {metric.trend === 'up' ? (
                      <TrendingUp color="success" fontSize="small" />
                    ) : (
                      <TrendingDown color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      color={metric.trend === 'up' ? 'success.main' : 'error.main'}
                      sx={{ ml: 0.5 }}
                    >
                      {metric.change > 0 ? '+' : ''}{metric.change}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* System Performance */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              System Performance
            </Typography>
            <List>
              {systemMetrics.map((metric, index) => (
                <ListItem key={index} divider={index < systemMetrics.length - 1}>
                  <ListItemText
                    primary={metric.name}
                    secondary={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(metric.value / metric.maxValue) * 100}
                            sx={{ flexGrow: 1, mr: 2 }}
                            color={getSystemStatusColor(metric.status) as any}
                          />
                          <Typography variant="body2">
                            {metric.value}{metric.unit}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                  <Chip
                    size="small"
                    label={metric.status}
                    color={getSystemStatusColor(metric.status) as any}
                    variant="outlined"
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Recent Activities */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Recent Activities
            </Typography>
            <List>
              {activities.map((activity: ActivityItem) => (
                <ListItem key={activity.id} divider>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'background.paper' }}>
                      {getActivityIcon(activity.type, activity.status)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={activity.title}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {activity.description}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(activity.timestamp)}
                          </Typography>
                          <Chip
                            size="small"
                            label={activity.impact}
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Alerts */}
      <Box sx={{ mt: 3 }}>
        <Alert severity="info" sx={{ mb: 1 }}>
          <strong>System Update:</strong> Scheduled maintenance window on Oct 20, 2:00 AM - 4:00 AM UTC.
        </Alert>
        <Alert severity="warning">
          <strong>Performance Notice:</strong> Memory usage is above normal levels. Consider optimizing resource allocation.
        </Alert>
      </Box>
        </>
      )}
    </Box>
  );
};

export default PerformanceDashboard;